import { lang } from '@umu/ukit-lang';
/*
 * @author: wayou
 * @date: 2018-09-04 15:40:16
 * @description: 图片轮播
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PinchZoom from 'pinch-zoom-js';
import { getFileSize, throttle } from '@umu/umu-util';
import { IGalleryImg } from '@umu/umu-types';
import ImgPreloader from './imgPreloader';
import ImgOriginalLoader from './imgOriginalLoader';
import If from '../if';
import Emg from '../emg/emg';
import { DEFAULT_IMG } from '../../images';
import { responsiveViewStyleHandleFn } from '../../services/responsiveStyle';
import './imgGallery.scss';
import { MemoEmg } from '../emg/memoEmg';

enum EmgFit {
    'default',
    'contain',
    'cover',
}

interface IImgGalleryProps {
    /**
     * toggle the visibility
     */
    isShow?: boolean;

    /**
     * 舞台宽度，默认
     */
    width?: number;

    /**
     * 舞台比例，默认会根据宽度撑满视区
     */
    ratio?: number;

    /**
     * 当前播放第 ${index} 张
     */
    index?: number;

    /**
     * 是否全屏展现
     */
    isFullScreen?: boolean;

    /**
     * 图片地址列表
     */
    imgList: IGalleryImg[];

    /**
     * 图片的适配方式
     */
    imgFit?: EmgFit;

    /**
     * 自定义转场动画的动画名，比如 [animate.css](https://github.com/daneden/animate.css/) 中的 `rotateIn`
     */
    transitionName?: string;
    disableDrag?: boolean;
    /**
     * 是否使用缓存的图片预览器
     */
    imgCache?: boolean;

    onSwipeChange?(isCanSwipe: boolean): void;
    onSizeChange?(size: { width: number; height: number }): void;
}

interface IImgGalleryState {
    stageWidth: number;
    stageHeight: number;
    loadedMap: { [key: string]: ELoadedResult };
}

enum ELoadedResult {
    LOADING,
    SUCCESS,
    FAIL,
}

export default class ImgGallery extends Component<IImgGalleryProps, IImgGalleryState> {
    public static defaultProps: Partial<IImgGalleryProps> = {
        isShow: true,
        index: 0,
        imgList: [],
        width: 320,
        ratio: 4 / 3,
        isFullScreen: false,
        transitionName: '',
    };
    private imgRefDiv?: HTMLDivElement;
    /**
     * 标识是否是首次展示，首次展示时把动画去掉以获得更好的体验
     */
    private isInitialShow: boolean = true;
    private pinchZoomMap: { [key: number]: any } = {};

    constructor(props: IImgGalleryProps) {
        super(props);
        this.state = {
            stageWidth: 0,
            stageHeight: 0,
            loadedMap: {},
        };
        this.initializeStage = throttle(this.initializeStage.bind(this), 200, this);
    }

    public componentDidMount() {
        setTimeout(() => {
            this.initializeStage();
            this.initializeEvents();
        }, 0);
        setTimeout(() => {
            this.initializePinchzoom();
        }, 200);
        this.preloadImages(this.props.imgList);
        if (this.props.isFullScreen) {
            responsiveViewStyleHandleFn(true);
        }
    }

    public componentDidUpdate() {
        if (this.props.isFullScreen) {
            responsiveViewStyleHandleFn(true);
        }
    }
    public componentWillUnmount() {
        window.removeEventListener('resize', this.initializeStage);
        const { index, imgList = [] } = this.props;
        const item = imgList[index || 0];
        item?.url && ImgOriginalLoader.cancel(item.url);
        responsiveViewStyleHandleFn(false);
    }
    public onImageClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        if (this.imgRefDiv) {
            const { index = 0 } = this.props;
            let hasVertical = false;
            const eleList = this.imgRefDiv.querySelectorAll('.img-slide-item-wrap') || [];
            const currentEle = eleList[index];
            hasVertical = currentEle && currentEle?.scrollHeight > currentEle?.clientHeight;
            hasVertical && e.stopPropagation && e.stopPropagation();
        }
    }
    public render() {
        const { index, imgList, isShow, transitionName } = this.props;
        const { stageWidth, stageHeight } = this.state;
        const left = this.getLeft(index!, stageWidth);

        if (!isShow) {
            return null;
        }

        // 首页不展示动画
        const style: React.CSSProperties = { left: `${left}px`, height: `${stageHeight}px` };
        if (this.isInitialShow && left !== 0) {
            style.transition = 'none';
            this.isInitialShow = false;
        }

        // 如果外部有自定义转场动画，也去掉这里的transition
        if (transitionName) {
            style.transition = 'none';
        }

        return (
            <div className="img-gallery" onClick={(e) => this.onImageClick(e)}>
                <div className="img-slides" style={style} ref={(ref) => (this.imgRefDiv = ref as HTMLDivElement)}>
                    {this.renderImgList(imgList, index!)}
                </div>
            </div>
        );
    }

    private preloadImages(list: IGalleryImg[]) {
        const urls = list.map((item) => {
            return item.thumb_url;
        });
        const loader = new ImgPreloader(urls);
        loader.start();
    }

    /**
     * 根据当前播放的序号计算滚动距离
     * @param index 当前播放的序号
     * @param width 舞台宽度
     */
    private getLeft(index: number, width: number): number {
        return -index * width;
    }

    /**
     * 初始舞台长宽
     */
    private initializeStage() {
        const { width, ratio, isFullScreen } = this.props;
        const stageWidth = this.gridWidth(width!);
        let stageHeight = +(stageWidth / ratio!).toFixed(0);
        if (isFullScreen) {
            stageHeight = window.innerHeight;
        }
        this.setState(
            {
                stageWidth,
                stageHeight,
            },
            () => {
                this.isInitialShow = false;
            },
        );

        const onSizeChange = this.props.onSizeChange;
        if (onSizeChange) {
            onSizeChange({
                width: stageWidth,
                height: stageHeight,
            });
        }
    }

    /**
     * 初始化窗口尺寸变化的监听
     */
    private initializeEvents() {
        window.addEventListener('resize', this.initializeStage);
    }

    private initializePinchzoom() {
        Array.from(document.querySelectorAll('.img-slide-item-wrap')).forEach((el, index) => {
            const pz = new PinchZoom(el as HTMLElement, { draggableUnzoomed: !this.props.disableDrag }) as any;
            this.pinchZoomMap[index] = pz;

            const { onSwipeChange } = this.props;
            if (!onSwipeChange) {
                return;
            }
            el.addEventListener(
                'pz_dragend',
                () => {
                    onSwipeChange(pz.offset.x === 0 || pz.offset.x === pz.el.clientWidth);
                },
                false,
            );
            el.addEventListener(
                'pz_zoomend',
                () => {
                    onSwipeChange(false);
                },
                false,
            );
        });
    }

    /**
     * 获取父级容器宽度
     * @param defaultWdith 默认宽度
     */
    private gridWidth(defaultWdith: number): number {
        try {
            const node = ReactDOM.findDOMNode(this);
            if (!node) {
                return defaultWdith;
            }
            const parent = node.parentElement;
            return parent && parent.offsetWidth ? parent.offsetWidth : defaultWdith;
        } catch (e) {
            return defaultWdith;
        }
    }

    /**
     * 渲染图片列表
     * @param imgList 图片地址列表
     */
    private renderImgList(imgList: IGalleryImg[], currentIndex: number) {
        const { stageWidth, stageHeight } = this.state;
        const { transitionName, imgFit } = this.props;
        const stageStyle = {
            width: `${stageWidth}px`,
            height: `${stageHeight}px`,
        };
        return imgList.map((item: IGalleryImg, index: number) => {
            const originalUrl = item.url;
            const thumbUrl = item.thumb_url;

            const loading = this.state.loadedMap[originalUrl] === ELoadedResult.LOADING;
            const loaded =
                this.state.loadedMap[originalUrl] === ELoadedResult.SUCCESS ||
                // 如果提供的数据只有一种图，认为都是原图
                originalUrl === thumbUrl ||
                // 如果是第二次渲染，直接从缓存里取状态
                ImgOriginalLoader.has(originalUrl);
            const size = item.file_size || 0;
            const EmgComponent = (this.props.imgCache ? MemoEmg : Emg) as typeof Emg;

            return (
                <div
                    key={index}
                    className={`img-slide-item animated ${currentIndex === index ? transitionName : ''}`}
                    style={stageStyle}
                >
                    <div className="img-slide-item-wrap">
                        <EmgComponent
                            className="img-slide"
                            src={loaded ? originalUrl : thumbUrl}
                            fallbackImg={DEFAULT_IMG}
                            loadingImg={DEFAULT_IMG}
                            fit={imgFit}
                        />
                    </div>
                    <If condition={!loaded}>
                        <div className="load-original">
                            <If condition={loading}>
                                <div className="load-status loading">{lang('下载中...')}</div>
                            </If>
                            <If condition={!loading}>
                                <div
                                    className="load-status do-load"
                                    onClick={(e) => this.loadOriginal(e, originalUrl, index)}
                                >
                                    {lang('查看原图')} {size ? getFileSize(size) : ''}
                                </div>
                            </If>
                        </div>
                    </If>
                </div>
            );
        });
    }

    private updateLoadedMap = (url: string, result: ELoadedResult) => {
        const loadedMap = Object.assign({}, this.state.loadedMap);
        loadedMap[url] = result;
        this.setState({
            loadedMap,
        });
    };

    private loadOriginal = (e: React.MouseEvent, url: string, index: number) => {
        e.preventDefault();
        e.stopPropagation();

        this.updateLoadedMap(url, ELoadedResult.LOADING);

        ImgOriginalLoader.load(url, {
            onSuccess: () => {
                this.updateLoadedMap(url, ELoadedResult.SUCCESS);
                this.resetPinchZoomFactor(index);
            },
        });
    };

    private resetPinchZoomFactor = (index: number) => {
        const pz = this.pinchZoomMap[index];
        if (!pz) {
            return;
        }
        try {
            const center = pz.getCurrentZoomCenter();
            pz.scaleTo(1.01, center);
        } catch (e) {
            console.error('[ImgGallery]:[resetPinchZoomFactor] resetPinchZoomFactor error:', e);
        }
    };
}
